/**
 * Explaining an access set (#5358 PR 15): for a hub, one of its agents and optionally a person, the
 * read and write sets every hub toolset call of that agent sends for that person, and, for each value
 * of each dimension, the layers that removed it.
 *
 *   GET /api/setup/hubs/:id/agents/:agentId/access-explain[?user_id=]
 *
 * It is worked out by the same functions a toolset call's set is (`apps/backend/src/rekor/access-set.ts`),
 * from the same layers, each read from the DO that owns it, so it cannot drift from what is sent, but
 * for one exception: explaining someone else matches no unredeemed team invite by their email
 * (`accessExplainQuery`), so a person holding one is shown without it.
 *   - with a person (`user_id`, or a person's own sign-in asking of themselves): their kind, first match
 *     wins (`ACCESS_PERSON_KINDS`, `./access-dimensions`), that kind's values ∩ the hub's ∩ the agent's: what a turn of theirs in
 *     their own conversation, on a channel that vouches for them, gets (that exception aside);
 *   - with no person (a scoped API token asking, with no `user_id`): the hub's ∩ the agent's, what a hub
 *     automation's turn of the agent gets.
 * A turn in another person's conversation is cut to that person's set too, and a delegated task's to the
 * set its delegation stored and its originators' current ones: those turns are not explained here.
 * The data product holds a call to its set only on record types that enforce a dimension (an `x-access`
 * entry in `enforce` mode); a type in `report` mode is not narrowed by it.
 */

import { z } from 'zod';
import { USER_ID_REGEX } from '../common';
import type { AccessAssignment, AccessPersonKind, AccessSet } from './access-dimensions';

/**
 * The layers a turn's set is the intersection of, in the order they are intersected. An explanation of a
 * hub, an agent and a person names `hub`, the person's kind (`hub_public` for everyone else: the hub's
 * public values) and `agent`; the rest bound turns with more parties: a consult's agent initiator
 * (`initiator_agent`), a delegated task's stored set (`delegation`) and its originators' current sets
 * (`originator`), and a background agent's host turn (`host_agent`).
 */
export const ACCESS_LAYERS = [
  'hub',
  'org_member',
  'hub_admin',
  'hub_team_member',
  'hub_user',
  'hub_public',
  'initiator_agent',
  'delegation',
  'originator',
  'host_agent',
  'agent',
] as const;
export type AccessLayer = (typeof ACCESS_LAYERS)[number];

/**
 * Whose set to explain: a platform account. Absent: the caller's own, or none for a caller that is no
 * person's interactive sign-in (a scoped API token). Someone else's unredeemed team invites are not
 * matched by their email: naming any account would otherwise tell whether its email is one an invite on
 * the hub names.
 */
export const accessExplainQuery = z.object({
  user_id: z.string().regex(USER_ID_REGEX).optional(),
});

/** One layer of the set, as it was intersected. */
export interface AccessExplainLayer {
  layer: AccessLayer;
  /**
   * The values it holds. An unset layer reaches nothing and shows as `{}`; null is an agent's with no
   * values, which narrows nothing.
   */
  values: AccessAssignment | null;
}

/** A value a set misses, and every layer whose list misses it. */
export interface AccessExplainRemoval {
  value_id: string;
  layers: AccessLayer[];
}

/** One dimension of the org's, or one a layer names. */
export interface AccessExplainDimension {
  dimension_id: string;
  /** The set's lists, as sent; both empty when the set leaves the dimension out: it reaches nothing there. */
  read: string[];
  write: string[];
  /** Each value of the dimension (the org's, retired ones included, and any a layer names) read misses. */
  read_removed: AccessExplainRemoval[];
  /** Each value read reaches that write misses, with the layers whose write lists miss it. */
  write_removed: AccessExplainRemoval[];
}

export interface AccessExplain {
  hub_id: string;
  /** The production hub whose values apply: the hub itself, its production hub, or null on a preview never published. */
  values_hub_id: string | null;
  agent_id: string;
  /** Whose set it is; null: no person, so the hub's ∩ the agent's. */
  user_id: string | null;
  /** The person's kind on the hub, first match wins; null with no person. */
  kind: AccessPersonKind | null;
  /**
   * The access set every hub toolset call of the agent sends for the person; for someone else holding an
   * unredeemed team invite, the set without it (`accessExplainQuery`).
   */
  set: AccessSet;
  /**
   * The set holds only on the record types customer scope narrows to one customer, on a call naming its
   * customer, and reaches nothing on every other type that enforces a dimension: the person is everyone
   * else. A call naming no customer sends `{}`.
   */
  customer_only: boolean;
  /**
   * The person's own set (the hub's ∩ their kind's values, before any agent's) reaches nothing. Every
   * turn in a conversation they are the person of is cut to it, whichever agent answers and a team
   * member's send or consult there included, so those reach nothing either.
   */
  conversations_reach_nothing: boolean;
  /** The layers intersected, in order. */
  layers: AccessExplainLayer[];
  dimensions: AccessExplainDimension[];
}

export interface AccessExplainResponse {
  data: AccessExplain;
}
